import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Headphones,
  Shield,
  Zap,
  Check,
  ExternalLink,
  Users,
  Clock,
  Wifi,
  MessageSquare,
} from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { productSchema } from '@/lib/seo';
import { ReviewForestWidget } from '@/components/ReviewForestWidget';
import { useTranslation } from '@/lib/i18n';

export default function TeamspeakPage() {
  const { t } = useTranslation();

  const products = [
    {
      id: 'ts3',
      icon: Headphones,
      name: t('teamspeak.ts3_name'),
      tagline: t('teamspeak.ts3_tagline'),
      price: '1.90',
      orderUrl: 'https://deinserverhost.de/store/cart.php?a=add&pid=379',
      badge: null,
      features: [
        t('teamspeak.ts3_f1'),
        t('teamspeak.ts3_f2'),
        t('teamspeak.ts3_f3'),
        t('teamspeak.ts3_f4'),
        t('teamspeak.ts3_f5'),
        t('teamspeak.ts3_f6'),
      ],
    },
  ];

  const highlights = [
    { icon: Shield,       key: 'hi_ddos' },
    { icon: Zap,          key: 'hi_setup' },
    { icon: Users,        key: 'hi_slots' },
    { icon: Clock,        key: 'hi_delivery' },
    { icon: Wifi,         key: 'hi_traffic' },
    { icon: MessageSquare, key: 'hi_prepaid' },
  ];

  const whyItems = [
    { key: 'why1', title: t('teamspeak.why1_title'), desc: t('teamspeak.why1_desc') },
    { key: 'why2', title: t('teamspeak.why2_title'), desc: t('teamspeak.why2_desc') },
    { key: 'why3', title: t('teamspeak.why3_title'), desc: t('teamspeak.why3_desc') },
  ];

  return (
    <Layout>
      <SEOHead
        title="TeamSpeak 3 Server Hosting – Voice Servers"
        description={`TeamSpeak 3 server hosting from €${products[0].price}/month: crystal-clear voice quality, DDoS protection and instant setup. Prepaid, no minimum contract.`}
        canonical="/teamspeak"
        schema={productSchema({
          name: 'TeamSpeak 3 Server',
          description: 'TeamSpeak 3 voice server hosting with DDoS protection and instant setup.',
          path: '/teamspeak',
          offers: products.map((product) => ({ name: product.name, price: product.price, period: 'month' })),
        })}
      />
      <div className="section-spacing">
        <div className="container-main">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-14"
          >
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="text-gradient">{t('teamspeak.title')}</span> {t('teamspeak.titleSuffix')}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              {t('teamspeak.subtitle')}
            </p>

            {/* Feature badges */}
            <div className="flex flex-wrap justify-center gap-3">
              {highlights.map(({ icon: Icon, key }) => (
                <div
                  key={key}
                  className="flex items-center gap-2 px-4 py-2 rounded-full glass border border-border hover:border-primary/30 transition-colors"
                >
                  <Icon className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-sm font-medium">{t(`teamspeak.${key}`)}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Product Card */}
          <div className="max-w-xl mx-auto mb-20">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-2xl border border-border hover:border-primary/30 transition-colors overflow-hidden flex flex-col"
                data-testid={`product-${product.id}`}
              >
                {/* Card Header */}
                <div className="px-8 pt-8 pb-6 border-b border-border">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                      <product.icon className="w-6 h-6 text-primary" />
                    </div>
                    {product.badge && (
                      <Badge className="bg-primary text-xs">{product.badge}</Badge>
                    )}
                  </div>
                  <h2 className="text-2xl font-black mb-1">{product.name}</h2>
                  <p className="text-sm text-muted-foreground">{product.tagline}</p>
                </div>

                {/* Features */}
                <div className="px-8 py-6 flex-1">
                  <ul className="space-y-3">
                    {product.features.map((f) => (
                      <li key={f} className="flex items-center gap-3 text-sm">
                        <Check className="w-4 h-4 text-green-500 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Price + CTA */}
                <div className="px-8 pb-8 pt-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-muted-foreground">{t('common.from')}</span>
                      <span className="text-4xl font-black text-primary ml-2">€{product.price}</span>
                      <span className="text-sm text-muted-foreground">{t('teamspeak.perMonth')}</span>
                    </div>
                    <a
                      href={product.orderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`order-${product.id}-btn`}
                    >
                      <Button className="bg-primary hover:bg-primary/90 glow-hover font-bold">
                        {t('teamspeak.orderNow')}
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Why section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold text-center mb-8">{t('teamspeak.whyTitle')}</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {whyItems.map((item) => (
                <div key={item.key} className="glass rounded-xl p-6 border border-border hover:border-primary/30 transition-colors">
                  <h3 className="font-bold text-sm mb-2">{item.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Reviews */}
          <ReviewForestWidget className="mb-12" />

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="flex flex-col sm:flex-row items-center justify-between gap-6 p-8 glass rounded-2xl"
          >
            <div>
              <h3 className="text-xl font-bold">{t('teamspeak.ctaTitle')}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t('teamspeak.ctaDesc')}
              </p>
            </div>
            <a
              href="https://deinserverhost.de/store/cart.php?a=add&pid=379"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover shrink-0">
                {t('teamspeak.ctaBtn')}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </motion.div>

        </div>
      </div>
    </Layout>
  );
}
